import React from 'react';
import { useTranslation } from 'react-i18next';

import { Span } from '../../components/Typography';

interface BundleSelectorProps {
  settings: BundleSetting[];
  selectedSetting: string;
  onUpdateSetting: (variantId: string) => void;
  hasDynamicSize: boolean;
  isPrepaid: boolean;
  originalSettings: BundleSetting[];
}

export const BundleSelector = ({
  settings,
  selectedSetting,
  onUpdateSetting,
  hasDynamicSize,
  isPrepaid,
  originalSettings,
}: BundleSelectorProps): JSX.Element => {
  const { t } = useTranslation();

  const getDynamicSizeLabel = ({
    MinSize,
    MaxSize,
  }: {
    MinSize?: string;
    MaxSize?: string;
  }) => {
    const minSize = MinSize ? parseFloat(MinSize) : 0;
    const maxSize = MaxSize ? parseFloat(MaxSize) : 0;
    const sizes = {
      min: minSize,
      max: maxSize,
    };
    return Object.keys(sizes)
      .reduce((acc, key) => {
        const size = sizes[key];
        if (size === 0) {
          return acc;
        }

        const label = t(`bundles.dynamicSize.${key}`, {
          count: size,
          item: t(
            `bundles.dynamicSize.item.${size > 1 ? 'plural' : 'singular'}`,
          ),
        });

        return [...acc, label];
      }, [] as string[])
      .join(', ');
  };

  return settings.length === 1 || isPrepaid ? (
    <div>
      <Span modifiers={['body']}>
        {hasDynamicSize
          ? getDynamicSizeLabel(settings[0])
          : getSettingLabel(originalSettings, selectedSetting)}
      </Span>
    </div>
  ) : (
    <div>
      {settings.length > 0 ? (
        <select
          value={selectedSetting}
          onChange={(event) => onUpdateSetting(event.target?.value)}
        >
          {settings.map((setting: BundleSetting) => (
            <option key={setting.VariantId} value={setting.VariantId}>
              {hasDynamicSize ? getDynamicSizeLabel(setting) : setting.Label}
            </option>
          ))}
        </select>
      ) : (
        getSettingLabel(originalSettings, selectedSetting)
      )}
    </div>
  );
};

const getSettingLabel = (
  settings: BundleSetting[],
  selectedSetting: string,
): string => {
  const setting = settings.find(
    (setting) => setting.VariantId === selectedSetting,
  );

  return setting ? setting.Label : '';
};
